/** Escape a value for use inside a CSS attribute selector (e.g. `[data-foo="…"]`). */
export function cssAttributeSelectorValue(value: string): string {
  if (typeof CSS !== "undefined" && "escape" in CSS) {
    return CSS.escape(value);
  }
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

export type ScheduleScrollMarketContextRowOptions = {
  /** Parent wrapper attribute, e.g. `data-market-context-situations-table`. */
  containerAttr?: string;
  /** Optional scope (e.g. active Radix tab panel) searched before `containerAttr`. */
  scopeSelector?: string;
  /** Row selector relative to the scope/container (or document if both missing). */
  rowSelector: string;
  maxAttempts?: number;
  attemptMs?: number;
};

/** Active Radix tab panel inside market buckets/situations tabs. */
export function marketContextActiveBucketsTabPanelSelector(): string {
  return '[data-market-context-buckets-tabs] [data-slot="tabs-content"][data-state="active"]';
}

let activeEvidenceScrollCleanup: (() => void) | null = null;

function findMarketContextScrollRow(options: ScheduleScrollMarketContextRowOptions): HTMLElement | null {
  const scope = options.scopeSelector ? document.querySelector(options.scopeSelector) : null;
  const container = options.containerAttr
    ? document.querySelector(`[${options.containerAttr}]`)
    : null;
  const root = scope ?? container;
  const row =
    root?.querySelector<HTMLElement>(options.rowSelector) ??
    (options.containerAttr
      ? document.querySelector<HTMLElement>(`[${options.containerAttr}] ${options.rowSelector}`)
      : null) ??
    document.querySelector<HTMLElement>(options.rowSelector);
  if (!row || !row.isConnected) return null;
  const rect = row.getBoundingClientRect();
  if (rect.height <= 0 && rect.width <= 0) return null;
  return row;
}

/** One smooth scroll to the target row (no intermediate panel scroll). */
function scrollRowIntoViewOnce(row: HTMLElement): void {
  row.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
}

/**
 * After deep-link navigation, wait for the target row then perform a single smooth scroll.
 * Cancels any prior pending evidence scroll so navigations do not stack animations.
 */
export function scheduleScrollMarketContextRow(
  options: ScheduleScrollMarketContextRowOptions,
): () => void {
  const { maxAttempts = 24, attemptMs = 80 } = options;
  let attempts = 0;
  let cancelled = false;
  let timeoutId: number | undefined;
  let didScroll = false;

  const cleanup = () => {
    cancelled = true;
    if (timeoutId != null) window.clearTimeout(timeoutId);
    if (activeEvidenceScrollCleanup === cleanup) {
      activeEvidenceScrollCleanup = null;
    }
  };

  activeEvidenceScrollCleanup?.();
  activeEvidenceScrollCleanup = cleanup;

  const tick = () => {
    if (cancelled || didScroll) return;
    const row = findMarketContextScrollRow(options);
    if (row) {
      didScroll = true;
      scrollRowIntoViewOnce(row);
      cleanup();
      return;
    }
    attempts += 1;
    if (attempts < maxAttempts) {
      timeoutId = window.setTimeout(tick, attemptMs);
    } else {
      cleanup();
    }
  };

  // Let the Market tab + sub-tab finish layout before the only scroll animation.
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(tick);
  });

  return cleanup;
}

/** Active shadcn table row inside a market-context table wrapper. */
export function marketContextActiveRowSelector(): string {
  return '[data-slot="table-row"][data-state="active"]';
}

/** Situation deep-link row inside situations table. */
export function marketContextSituationRowSelector(situation: string): string {
  const escaped = cssAttributeSelectorValue(situation.trim());
  return `[data-slot="table-row"][data-market-context-situation="${escaped}"]`;
}

/** Bucket deep-link row inside bucket table. */
export function marketContextBucketRowSelector(bucket: string): string {
  const escaped = cssAttributeSelectorValue(bucket.trim());
  return `[data-slot="table-row"][data-market-context-bucket="${escaped}"]`;
}
